import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import Image from '@atlaskit/image';

import type {
	HubElementLayout,
	HubElement,
} from '../hub-element-placeholder/config-panel/hubElementType';

import { layoutOptions, spotlightExtensionKey, spotlightExtensionType } from './spotlightConstants';
import spotlightHubElementThumbnail from './spotlightHubElementThumbmail.svg';
import spotlightHubElementThumbnailDark from './spotlightHubElementThumbmail-dark.svg';
import { spotlightParameters } from './spotlightManifest';
import type { SpotlightParameters } from './spotlightTypes';
import { CommonLayout } from './spotlightTypes';
import { Spotlight } from './view/Spotlight';

const i18n = defineMessages({
	spotlightTitle: {
		id: 'custom-sites-extensions.spotlight.hub-element.title',
		defaultMessage: 'Spotlight',
		description: 'A visible text label to be used as the title for the spotlight element',
	},
	spotlightDescription: {
		id: 'custom-sites-extensions.spotlight.hub-element.description',
		defaultMessage: 'Highlight important messages',
		description: 'A visible text label to be used as the description for the spotlight element',
	},
});

const getDefaultSpotlightNodeAdf = (intl: IntlShape, layout: CommonLayout): ADFEntity => {
	return {
		type: 'extension',
		attrs: {
			extensionKey: spotlightExtensionKey,
			extensionType: spotlightExtensionType,
			parameters: { ...spotlightParameters(intl), layout },
		},
	};
};
const thumbnailImage: React.ReactNode = (
	<Image src={spotlightHubElementThumbnail} srcDark={spotlightHubElementThumbnailDark} alt="" />
);

const getLayoutPreviewComponent = (intl: IntlShape, layout: CommonLayout) => {
	return (
		<Spotlight
			extensionNode={
				getDefaultSpotlightNodeAdf(intl, layout).attrs as ExtensionParams<SpotlightParameters>
			}
			contentId=""
		/>
	);
};

const getHubElementLayouts = (): HubElementLayout[] => {
	return layoutOptions.map((layout) => ({
		...layout,
		getNodeAdf: (intl: IntlShape) => getDefaultSpotlightNodeAdf(intl, layout.value),
		getPreviewComponent: (intl: IntlShape) => getLayoutPreviewComponent(intl, layout.value),
	}));
};

export const spotlightHubElement: HubElement = {
	extensionKey: spotlightExtensionKey,
	title: i18n.spotlightTitle,
	description: i18n.spotlightDescription,
	Image: thumbnailImage,
	layouts: getHubElementLayouts(),
	hasNewLozenge: true,
	getNodeAdf: (intl: IntlShape) => getDefaultSpotlightNodeAdf(intl, CommonLayout.SPLIT_IMAGE_RIGHT),
};
